import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { login, setToken } from '../lib/auth';
import { formatPhone, isValidPhone, toApiPhone } from '../lib/phone';
import BrutalistNavbar from '../components/layout/BrutalistNavbar';
import BrutalistFooter from '../components/layout/BrutalistFooter';

const API_BASE = process.env.REACT_APP_BACKEND_URL ?? '';

export default function LoginPage() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Состояние блока восстановления
  const [mode, setMode] = useState('login');   // login | forgot | reset
  const [code, setCode] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [channel, setChannel] = useState('');  // call | sms | fake

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    if (!isValidPhone(phone)) { setError('Введите телефон полностью (11 цифр)'); setBusy(false); return; }
    try {
      await login(toApiPhone(phone), password);
      navigate('/cabinet');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Не удалось войти';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const sendForgot = async () => {
    setError('');
    if (!isValidPhone(phone)) { setError('Введите телефон полностью'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/password/forgot`, { phone: toApiPhone(phone) });
      setChannel(data.channel);
      setMode('reset');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось отправить код');
    } finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPwd.length < 6) { setError('Пароль не короче 6 символов'); return; }
    if (newPwd !== newPwd2) { setError('Пароли не совпадают'); return; }
    if (code.length < 4) { setError('Введите код из звонка/SMS'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/password/reset`, {
        phone: toApiPhone(phone), code: code.trim(), new_password: newPwd,
      });
      if (data?.token) setToken(data.token);
      navigate('/cabinet');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось сменить пароль');
    } finally { setBusy(false); }
  };

  const inputClass = "w-full bg-gray-50 border-2 border-black rounded-none px-4 py-3 font-mono text-sm placeholder:text-gray-500 focus:outline-none focus:bg-white";
  const labelClass = "block text-xs font-bold uppercase tracking-wider mb-2 font-mono text-black/60";

  return (
    <div className="min-h-screen bg-[#F5F3EE] flex flex-col">
      <BrutalistNavbar />
      <main className="flex-1 flex items-start justify-center px-4 pt-32 pb-12">
        <div className="bg-white border-2 border-black p-8 shadow-[8px_8px_0_0_#000] w-full max-w-md">
          {mode === 'login' && (
            <>
              <h1 className="text-3xl font-bold uppercase mb-2 text-center">Вход в кабинет</h1>
              <p className="font-mono text-xs text-black/60 mb-8 text-center">Введите телефон и пароль</p>
              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <label className={labelClass}>Телефон</label>
                  <input
                    type="tel" inputMode="numeric" autoComplete="tel" maxLength={18}
                    className={inputClass} value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder="+7 (___) ___-__-__" required
                  />
                </div>
                <div>
                  <label className={labelClass}>Пароль</label>
                  <input type="password" className={inputClass} value={password}
                    onChange={(e) => setPassword(e.target.value)} required minLength={6} />
                  <button type="button" onClick={() => { setError(''); setMode('forgot'); }}
                    className="mt-1 text-[11px] font-mono text-black/60 underline hover:text-[#E63B2E]">
                    Забыли пароль?
                  </button>
                </div>
                {error && <p className="text-xs text-red-600 font-mono uppercase">// {error}</p>}
                <button type="submit" disabled={busy}
                  className="w-full h-14 bg-[#E63B2E] hover:bg-[#D62B1E] disabled:bg-gray-300 text-white rounded-none border-2 border-black uppercase font-bold tracking-widest text-sm transition-all hover:translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_#000]">
                  {busy ? 'Вход...' : 'Войти'}
                </button>
                <p className="text-center font-mono text-xs text-black/60 pt-2">
                  Нет аккаунта? <Link to="/register" className="underline hover:text-accent">Зарегистрироваться</Link>
                </p>
              </form>
            </>
          )}

          {mode === 'forgot' && (
            <>
              <h1 className="text-3xl font-bold uppercase mb-2 text-center">Восстановление</h1>
              <p className="font-mono text-xs text-black/60 mb-6 text-center">
                Введите телефон — пришлём звонок (последние 4 цифры номера = ваш код) или SMS.
              </p>
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Телефон</label>
                  <input type="tel" inputMode="numeric" maxLength={18}
                    className={inputClass} value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder="+7 (___) ___-__-__" />
                </div>
                {error && <p className="text-xs text-red-600 font-mono uppercase">// {error}</p>}
                <button onClick={sendForgot} disabled={busy || !isValidPhone(phone)}
                  className="w-full h-14 bg-black disabled:bg-gray-300 text-white border-2 border-black uppercase font-bold tracking-widest text-sm hover:bg-[#E63B2E] transition-colors">
                  {busy ? 'Отправка...' : 'Прислать код'}
                </button>
                <button onClick={() => { setError(''); setMode('login'); }}
                  className="w-full text-xs font-mono text-black/60 underline hover:text-[#E63B2E]">
                  ← Назад ко входу
                </button>
              </div>
            </>
          )}

          {mode === 'reset' && (
            <>
              <h1 className="text-3xl font-bold uppercase mb-2 text-center">Новый пароль</h1>
              <p className="font-mono text-xs text-black/60 mb-6 text-center">
                {channel === 'call'
                  ? 'Сейчас придёт звонок — введите последние 4 цифры номера звонящего.'
                  : channel === 'sms'
                    ? 'На телефон отправлен 6-значный код.'
                    : 'Если телефон зарегистрирован — придёт звонок или SMS с кодом.'}
              </p>
              <form onSubmit={doReset} className="space-y-5">
                <div>
                  <label className={labelClass}>Код</label>
                  <input type="text" inputMode="numeric" maxLength={6}
                    className={inputClass} value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="4 или 6 цифр" autoFocus />
                </div>
                <div>
                  <label className={labelClass}>Новый пароль</label>
                  <input type="password" className={inputClass} value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)} minLength={6} required
                    autoComplete="new-password" />
                </div>
                <div>
                  <label className={labelClass}>Повторите</label>
                  <input type="password" className={inputClass} value={newPwd2}
                    onChange={(e) => setNewPwd2(e.target.value)} minLength={6} required
                    autoComplete="new-password" />
                </div>
                {error && <p className="text-xs text-red-600 font-mono uppercase">// {error}</p>}
                <button type="submit" disabled={busy}
                  className="w-full h-14 bg-[#E63B2E] disabled:bg-gray-300 text-white border-2 border-black uppercase font-bold tracking-widest text-sm hover:bg-[#D62B1E]">
                  {busy ? 'Сохранение...' : 'Сменить пароль'}
                </button>
                <button type="button" onClick={() => { setError(''); setMode('login'); }}
                  className="w-full text-xs font-mono text-black/60 underline hover:text-[#E63B2E]">
                  ← Отмена
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <BrutalistFooter />
    </div>
  );
}
